import { useState, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import Lottie from 'lottie-react';

import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';
import { useLanguage } from '../../../Context/LanguageContext';

import { ReactComponent as RealtimeCalcIcon } from '../../../image/RealtimeCalcIcon.svg';
import LoadingAnimation from '../../../Animation/NewLoading.json';

export default function DurationBox(props) {
  const {
    calcDateText,
    startDate,
    endDate,
    revenue,
    durationAvg,
    durationMembers,
    allMembers,
    isOpenPartnerGymSales,
    isDurationLoading
  } = props;

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  // 월평균 매출 계산: revenue(sales/revenue/year) 데이터 YTD 기준 //
  // 과거 연도: 1월 ~ 12월 (전체) / 올해: 1월 ~ 현재 달까지 //
  const priceAvg = useMemo(() => {
    if (!revenue || revenue.length === 0 || !startDate) return 0;

    const now = moment();
    const targetYear = startDate.year();
    const lastMonth = targetYear === now.year() ? now.month() + 1 : 12;

    let totalSum = 0;

    for (let i = 0; i < revenue.length; i++) {
      const sale = revenue[i];

      if (!sale.approvalDate) continue;

      const year = parseInt(sale.approvalDate.slice(0, 4));
      const month = parseInt(sale.approvalDate.slice(5, 7));

      if (year === targetYear && month >= 1 && month <= lastMonth) {
        totalSum += sale.totalPrice;
      }
    }

    return Math.round(totalSum / lastMonth);
  }, [revenue, startDate]);

  // 산정기간 회원권 overlap 회원만 포함하는 유효 회원 목록 //
  const filteredDurationMembers = useMemo(() => {
    if (!startDate || !endDate || !Array.isArray(allMembers) || allMembers.length === 0) return durationMembers;

    const startTs = moment(startDate).startOf('day').valueOf();
    const endTs = moment(endDate).endOf('day').valueOf();

    const validMemberIds = new Set(
      allMembers
        .filter(m => Array.isArray(m.usages) && m.usages.some(u =>
          moment(u.startDate).startOf('day').valueOf() <= endTs
          && moment(u.endDate).startOf('day').valueOf() >= startTs
        ))
        .map(m => m.memberId)
    );

    return durationMembers.filter(d => validMemberIds.has(d.memberId));
  }, [durationMembers, allMembers, startDate, endDate]);

  const [revenueAvg, setRevenueAvg] = useState(priceAvg);
  const [members, setMembers] = useState(filteredDurationMembers.length);
  const [duration, setDuration] = useState(durationAvg);
  const [isDragging, setIsDragging] = useState(false);

  const sliderMaxRef = useRef({ revenueAvg: 0, members: 0, duration: 0 });
  const draggingRef = useRef(null);
  const revenueAvgTrackRef = useRef(null);
  const membersTrackRef = useRef(null);
  const durationTrackRef = useRef(null);

  // 슬라이더 최댓값: props 초기값의 2배 //
  const sliderMax = {
    revenueAvg: priceAvg * 2,
    members: filteredDurationMembers.length * 2,
    duration: durationAvg * 2
  };
  sliderMaxRef.current = sliderMax;

  const trackRefMap = {
    revenueAvg: revenueAvgTrackRef,
    members: membersTrackRef,
    duration: durationTrackRef
  };

  const setterMap = {
    revenueAvg: setRevenueAvg,
    members: setMembers,
    duration: setDuration
  };

  // props 변경 시 매출 state 동기화 //
  useEffect(() => {
    setRevenueAvg(priceAvg);
  }, [priceAvg]);

  // props 변경 시 회원 수 state 동기화 //
  useEffect(() => {
    setMembers(filteredDurationMembers.length);
  }, [filteredDurationMembers]);

  // props 변경 시 지속 기간 state 동기화 //
  useEffect(() => {
    setDuration(durationAvg);
  }, [durationAvg]);

  // 슬라이더 드래그 이벤트 등록 //
  useEffect(() => {
    const onMouseMove = (e) => {
      if (!draggingRef.current) return;

      const value = calcValueFromMouse(e.clientX, draggingRef.current);

      setterMap[draggingRef.current](value);
    };

    const onMouseUp = () => {
      draggingRef.current = null;
      setIsDragging(false);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  // 마우스 위치를 슬라이더 값으로 환산 //
  function calcValueFromMouse(clientX, type) {
    const track = trackRefMap[type].current;

    if (!track) return 0;

    const rect = track.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));

    return Math.round(ratio * sliderMaxRef.current[type]);
  }

  // 슬라이더 드래그 시작 //
  function onMouseDownSlider(type) {
    return (e) => {
      e.preventDefault();
      draggingRef.current = type;
      setIsDragging(true);

      const value = calcValueFromMouse(e.clientX, type);

      setterMap[type](value);
    };
  }

  // 초기값 복원 버튼 클릭 //
  function onClickResetButton() {
    setRevenueAvg(priceAvg);
    setMembers(filteredDurationMembers.length);
    setDuration(durationAvg);
  }

  // 1인당 월 매출 계산 //
  function calcRevenuePerMember() {
    if (members === 0 || revenueAvg === 0) return 0;

    return Math.round(revenueAvg / members);
  }

  // LTV 계산 //
  function calcLTV() {
    if (members === 0 || revenueAvg === 0 || duration === 0) return 0;

    return Math.round((revenueAvg / members) * duration);
  }

  // 권장 최대 광고비 계산 //
  function calcMaxAdSpend() {
    return Math.round(calcLTV() * 0.2);
  }

  // 전체 회원 총 매출 계산 //
  function calcTotalRevenue() {
    return calcLTV() * members;
  }

  // 슬라이더 위치 비율 계산 //
  function calcSliderPosition(value, max) {
    if (max === 0) return 0;

    const ratio = Math.min(value / max, 1);

    return ratio * 100;
  }

  const ltv = calcLTV();
  const revenueAvgPerMember = calcRevenuePerMember();
  const maxAdSpend = calcMaxAdSpend();
  const totalRevenue = calcTotalRevenue();

  return (
    <TopLevelWrapper $isDark={isDark}>
      {
        isDurationLoading ?
          <AnimationBox>
            <LottieWrapper>
              <Lottie animationData={LoadingAnimation} loop={true} />
            </LottieWrapper>
          </AnimationBox>
          :
          isOpenPartnerGymSales ?
            <ContentWrapper>
              {/* 타이틀 */}
              <TitleRow>
                <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.text}>
                  {calcDateText() + ' LIFETIME VALUE'}
                </Text>
              </TitleRow>
              <SubtitleRow>
                <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.text}>
                  {t('statistics.duration.subtitle')}
                </Text>
              </SubtitleRow>

              {/* 메인 콘텐츠: 왼쪽(LTV) + 오른쪽(슬라이더) */}
              <MainContentRow>
                {/* 왼쪽: LTV 큰 값 + 계산식 */}
                <LeftSection>
                  <LTVLabelWrapper>
                    <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
                      {t('statistics.duration.totalRevenuePerCustomer')}
                    </Text>
                  </LTVLabelWrapper>
                  <LTVValueWrapper>
                    {
                      t('statistics.duration.currencyPrefix') &&
                      <Text fontFamily='NotoSansKR-Regular' fontSize={20} color={theme.text}>
                        {t('statistics.duration.currencyPrefix')}
                      </Text>
                    }
                    <LTVBigValue fontFamily='NotoSansKR-Bold' fontSize={70} color={theme.subKey}>
                      {ltv.toLocaleString('ko-KR')}
                    </LTVBigValue>
                    {
                      t('statistics.duration.currencySuffix') &&
                      <Text fontFamily='NotoSansKR-Regular' fontSize={20} color={theme.text} style={{ alignSelf: 'flex-end' }}>
                        {t('statistics.duration.currencySuffix')}
                      </Text>
                    }
                  </LTVValueWrapper>
                  <RecommendedAdText>
                    <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
                      {t('statistics.duration.recommendedAdSpendBefore')}
                    </Text>
                    <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subKey}>
                      {t('statistics.duration.currencyPrefix') ? t('statistics.duration.currencyPrefix') + ' ' + maxAdSpend.toLocaleString('ko-KR') : maxAdSpend.toLocaleString('ko-KR') + t('statistics.duration.currencySuffix')}
                    </Text>
                    {
                      t('statistics.duration.recommendedAdSpendAfter') &&
                      <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
                        {t('statistics.duration.recommendedAdSpendAfter')}
                      </Text>
                    }
                  </RecommendedAdText>

                  {/* 계산식 박스 */}
                  <FormulaBox backgroundColor={theme.buttonFill}>
                    <FormulaCell>
                      <Text fontFamily='NotoSansKR-Bold' fontSize={18} color={theme.text}>
                        {revenueAvg.toLocaleString('ko-KR')}
                      </Text>
                      <FormulaDivider backgroundColor={theme.text} />
                      <Text fontFamily='NotoSansKR-Bold' fontSize={18} color={theme.text}>
                        {members.toLocaleString('ko-KR')}
                      </Text>
                      <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.text}>
                        {startDate.format(t('statistics.dateFormatYearShort')) + ' ' + t('statistics.duration.formulaLabel')}
                      </Text>
                    </FormulaCell>
                    <FormulaOperator>
                      <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.line}>x</Text>
                    </FormulaOperator>
                    <FormulaCell>
                      <Text fontFamily='NotoSansKR-Bold' fontSize={18} color={theme.text}>
                        {duration.toLocaleString('ko-KR')}
                      </Text>
                      <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.text}>
                        {t('statistics.duration.formulaMonths')}
                      </Text>
                    </FormulaCell>
                    <FormulaOperator>
                      <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.line}>=</Text>
                    </FormulaOperator>
                    <FormulaResultCell>
                      <FormulaResultValueRow>
                        {
                          t('statistics.duration.currencyPrefix') &&
                          <Text fontFamily='NotoSansKR-Bold' fontSize={12} color={theme.text}>
                            {t('statistics.duration.currencyPrefix')}
                          </Text>
                        }
                        <Text fontFamily='NotoSansKR-Bold' fontSize={18} color={theme.subKey}>
                          {ltv.toLocaleString('ko-KR')}
                        </Text>
                        {
                          t('statistics.duration.currencySuffix') &&
                          <Text fontFamily='NotoSansKR-Bold' fontSize={12} color={theme.text}>
                            {t('statistics.duration.currencySuffix')}
                          </Text>
                        }
                      </FormulaResultValueRow>
                      <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.text}>
                        LTV
                      </Text>
                    </FormulaResultCell>
                  </FormulaBox>
                </LeftSection>

                {/* 오른쪽: 슬라이더 3개 */}
                <RightSection>
                  <BadgeRow>
                    <RealtimeButton onClick={onClickResetButton}>
                      <StyledRealtimeCalcIcon width={16} height={16} $color={theme.subBackground} />
                      <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subBackground} style={{ cursor: 'pointer' }}>
                        {t('statistics.duration.realtimeLtvCalc')}
                      </Text>
                    </RealtimeButton>
                  </BadgeRow>
                  <SliderRow>
                    <SliderLabel>
                      <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
                        {startDate.format(t('statistics.dateFormatYearShort')) + ' ' + t('statistics.duration.monthlyAvgSales')}
                      </Text>
                    </SliderLabel>
                    <SliderValue>
                      {
                        t('statistics.duration.currencyPrefix') &&
                        <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.subText}>
                          {t('statistics.duration.currencyPrefix')}
                        </Text>
                      }
                      <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.text}>
                        {revenueAvg.toLocaleString('ko-KR')}
                      </Text>
                      {
                        t('statistics.duration.currencySuffix') &&
                        <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.subText}>
                          {t('statistics.duration.currencySuffix')}
                        </Text>
                      }
                    </SliderValue>
                  </SliderRow>
                  <SliderTrackWrapper
                    ref={trackRefMap.revenueAvg}
                    onMouseDown={onMouseDownSlider('revenueAvg')}
                    $isDragging={isDragging}
                  >
                    <SliderTrack backgroundColor={theme.line} />
                    <SliderThumb
                      left={calcSliderPosition(revenueAvg, sliderMax.revenueAvg)}
                      borderColor={theme.subKey}
                      backgroundColor={theme.subBackground}
                      $isDragging={isDragging && draggingRef.current === 'revenueAvg'}
                    />
                  </SliderTrackWrapper>

                  <SliderRow>
                    <SliderLabel>
                      <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
                        {t('statistics.duration.validMembers')}
                      </Text>
                    </SliderLabel>
                    <SliderValue>
                      <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.text}>
                        {members.toLocaleString('ko-KR')}
                      </Text>
                      <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.subText}>
                        {t('statistics.duration.unitMembers')}
                      </Text>
                    </SliderValue>
                  </SliderRow>
                  <SliderTrackWrapper
                    ref={trackRefMap.members}
                    onMouseDown={onMouseDownSlider('members')}
                    $isDragging={isDragging}
                  >
                    <SliderTrack backgroundColor={theme.line} />
                    <SliderThumb
                      left={calcSliderPosition(members, sliderMax.members)}
                      borderColor={theme.subKey}
                      backgroundColor={theme.subBackground}
                      $isDragging={isDragging && draggingRef.current === 'members'}
                    />
                  </SliderTrackWrapper>

                  <SliderRow>
                    <SliderLabel>
                      <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
                        {t('statistics.duration.avgMembershipDuration')}
                      </Text>
                    </SliderLabel>
                    <SliderValue>
                      <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.text}>
                        {duration.toLocaleString('ko-KR')}
                      </Text>
                      <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.subText}>
                        {t('statistics.duration.unitMonths')}
                      </Text>
                    </SliderValue>
                  </SliderRow>
                  <SliderTrackWrapper
                    ref={trackRefMap.duration}
                    onMouseDown={onMouseDownSlider('duration')}
                    $isDragging={isDragging}
                  >
                    <SliderTrack backgroundColor={theme.line} />
                    <SliderThumb
                      left={calcSliderPosition(duration, sliderMax.duration)}
                      borderColor={theme.subKey}
                      backgroundColor={theme.subBackground}
                      $isDragging={isDragging && draggingRef.current === 'duration'}
                    />
                  </SliderTrackWrapper>
                </RightSection>
              </MainContentRow>

              {/* 하단 3개 카드 */}
              <BottomCardRow>
                <BottomCard backgroundColor={theme.buttonFill}>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
                    {t('statistics.duration.recommendedMaxAdSpend')}
                  </Text>
                  <BottomCardValueRow>
                    {
                      t('statistics.duration.currencyPrefix') &&
                      <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.subText}>
                        {t('statistics.duration.currencyPrefix')}
                      </Text>
                    }
                    <Text fontFamily='NotoSansKR-Bold' fontSize={18} color={theme.subKey}>
                      {maxAdSpend.toLocaleString('ko-KR')}
                    </Text>
                    {
                      t('statistics.duration.currencySuffix') &&
                      <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.subText}>
                        {t('statistics.duration.currencySuffix')}
                      </Text>
                    }
                  </BottomCardValueRow>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
                    {t('statistics.duration.keepWithin30')}
                  </Text>
                </BottomCard>

                <BottomCard backgroundColor={theme.buttonFill}>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
                    {t('statistics.duration.revenueAvgPerMemberMonth')}
                  </Text>
                  <BottomCardValueRow>
                    {
                      t('statistics.duration.currencyPrefix') &&
                      <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.subText}>
                        {t('statistics.duration.currencyPrefix')}
                      </Text>
                    }
                    <Text fontFamily='NotoSansKR-Bold' fontSize={18} color={theme.subKey}>
                      {revenueAvgPerMember.toLocaleString('ko-KR')}
                    </Text>
                    {
                      t('statistics.duration.currencySuffix') &&
                      <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.subText}>
                        {t('statistics.duration.currencySuffix')}
                      </Text>
                    }
                  </BottomCardValueRow>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
                    {t('statistics.duration.monthlyValuePerMember')}
                  </Text>
                </BottomCard>

                <BottomCard backgroundColor={theme.buttonFill}>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
                    {t('statistics.duration.totalRevenueAllMembers')}
                  </Text>
                  <BottomCardValueRow>
                    {
                      t('statistics.duration.currencyPrefix') &&
                      <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.subText}>
                        {t('statistics.duration.currencyPrefix')}
                      </Text>
                    }
                    <Text fontFamily='NotoSansKR-Bold' fontSize={18} color={theme.subKey}>
                      {totalRevenue.toLocaleString('ko-KR')}
                    </Text>
                    {
                      t('statistics.duration.currencySuffix') &&
                      <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.subText}>
                        {t('statistics.duration.currencySuffix')}
                      </Text>
                    }
                  </BottomCardValueRow>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
                    {t('statistics.duration.ltvTimesAllMembers')}
                  </Text>
                </BottomCard>
              </BottomCardRow>
            </ContentWrapper>
            :
            <NotOpenWrapper>
              <Text fontFamily='NotoSansKR-Bold' fontSize={15} color={theme.subText}>{t('statistics.partnerGymPrivate')}</Text>
              <Text fontFamily='NotoSansKR-Regular' fontSize={15} color={theme.subText}>{t('statistics.partnerGymPrivateDesc')}</Text>
            </NotOpenWrapper>
      }
    </TopLevelWrapper>
  );
}

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;
  justify-content: center;

  margin: 38px 0;
  padding: 20px 40px;

  width: 100%;
  max-width: 960px;

  gap: 10px;

  border-radius: 20px;
  box-shadow: ${props => props.$isDark ? 'none' : '0 40px 50px rgba(240, 232, 227, 0.5)'};
  background-color: ${props => props.theme.subBackground};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;
  justify-content: center;

  width: 100%;

  gap: 10px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  overflow: hidden;
  width: 100%;
`;

const SubtitleRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const MainContentRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: 100%;
`;

const LeftSection = styled.div`
  display: flex;
  flex-direction: column;

  align-items: center;
  justify-content: center;

  padding: 30px 10px;

  gap: 10px;

  width: calc(50% - 10px);
`;

const LTVLabelWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const LTVValueWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  gap: 5px;
`;

const LTVBigValue = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;
  line-height: ${props => props.fontSize}px;

  color: ${props => props.color};
  margin: 0;
  cursor: default;
`;

const RecommendedAdText = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  justify-content: center;

  padding: 10px 0;
  gap: 4px;
`;

const FormulaBox = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  justify-content: center;

  width: calc(100% - 20px);

  border-radius: 10px;
  background-color: ${props => props.backgroundColor};

  overflow: hidden;
`;

const FormulaCell = styled.div`
  display: flex;
  flex-direction: column;

  align-items: center;
  justify-content: center;

  padding: 10px;
  height: 105px;

  border-radius: 10px;
`;

const FormulaDivider = styled.div`
  width: 100%;
  height: 1px;
  margin: 2px 0;

  border-radius: 100px;
  background-color: ${props => props.backgroundColor};
`;

const FormulaOperator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
`;

const FormulaResultCell = styled.div`
  display: flex;
  flex-direction: column;

  align-items: center;
  justify-content: center;

  padding: 0 10px;

  border-radius: 10px;
`;

const FormulaResultValueRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  gap: 5px;
`;

const StyledRealtimeCalcIcon = styled(RealtimeCalcIcon)`
  path {
    stroke: ${props => props.$color};
  }
`;

const BadgeRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;

  width: 100%;
  padding: 5px 0;
`;

const RealtimeButton = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  justify-content: center;

  width: auto;
  height: auto;

  padding: 0 5px;
  gap: 2px;

  border: 5px;
  border-radius: 5px;
  background-color: ${props => props.theme.subKey};
  cursor: pointer;
`;

const RightSection = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  width: calc(50% + 10px);
`;

const SliderRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  height: 50px;
  width: 100%;
`;

const SliderLabel = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
`;

const SliderValue = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  gap: 5px;
`;

const SliderTrackWrapper = styled.div`
  position: relative;

  display: flex;
  align-items: center;

  width: 100%;
  height: 30px;

  cursor: pointer;
  user-select: none;
`;

const SliderTrack = styled.div`
  width: 100%;
  height: 2px;
  background-color: ${props => props.backgroundColor};
`;

const SliderThumb = styled.div`
  position: absolute;
  left: ${props => props.left}%;

  width: 22px;
  height: 22px;

  border: 4px solid ${props => props.borderColor};
  border-radius: 1000px;
  background-color: ${props => props.backgroundColor};

  transform: translateX(-50%);
  cursor: ${props => props.$isDragging ? 'grabbing' : 'grab'};
`;

const BottomCardRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  width: 100%;
  gap: 20px;
`;

const BottomCard = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;

  justify-content: center;
  align-items: flex-start;

  padding: 10px 20px;

  gap: 5px;

  border-radius: 8px;
  background-color: ${props => props.backgroundColor};

  overflow: hidden;
  white-space: nowrap;
`;

const BottomCardValueRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  gap: 5px;
`;

const NotOpenWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  padding: 40px 0;
`;

const AnimationBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 300px;
`;

const LottieWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 160px;
  height: 160px;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0;
  cursor: default;
`;